/**
 * Object storage for dish photos and restaurant branding.
 *
 * One public-read bucket, `restaurant-media`. Object keys are always
 * `<restaurant_id>/<menu|brand>/<uuid>.<ext>`, which is what lets the storage
 * policy authorise a write from the path alone — see migration 0008.
 */

import { ApiError, type DbClient } from './client';

export const MEDIA_BUCKET = 'restaurant-media';

export type MediaKind = 'menu' | 'brand';

export interface UploadedMedia {
  /** Object key within the bucket, stored on the row for later deletion. */
  path: string;
  /** Public URL, safe to embed in an `<Image source>` or an `<img>`. */
  publicUrl: string;
}

const EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/avif': 'avif',
  'image/heic': 'heic',
};

/**
 * The public URL for an object in the media bucket.
 *
 * @param client A Supabase client.
 * @param path Object key.
 * @returns A public URL.
 */
export function publicMediaUrl(client: DbClient, path: string): string {
  return client.storage.from(MEDIA_BUCKET).getPublicUrl(path).data.publicUrl;
}

/**
 * Uploads a photo.
 *
 * The bucket enforces a 5 MiB limit and an image MIME allow-list; the checks
 * here exist so the merchant gets a clear message before the bytes are sent.
 *
 * @param client A Supabase client.
 * @param restaurantId Owning restaurant; becomes the first path segment.
 * @param file The image to upload.
 * @param kind Whether this is a dish photo or branding.
 * @returns The stored path and its public URL.
 * @throws ApiError when the file is too large or not an accepted image type.
 */
export async function uploadMedia(
  client: DbClient,
  restaurantId: string,
  file: File | Blob,
  kind: MediaKind,
): Promise<UploadedMedia> {
  const contentType = file.type || 'image/jpeg';
  const extension = EXTENSIONS[contentType];

  if (!extension) {
    throw new ApiError(
      'Please choose a JPEG, PNG, WebP, AVIF, or HEIC image.',
      'check_violation',
    );
  }

  if (file.size > 5 * 1024 * 1024) {
    throw new ApiError('That image is larger than 5 MB. Please choose a smaller one.', 'check_violation');
  }

  const path = `${restaurantId}/${kind}/${crypto.randomUUID()}.${extension}`;

  const { error } = await client.storage.from(MEDIA_BUCKET).upload(path, file, {
    cacheControl: '31536000',
    upsert: false,
    contentType,
  });

  if (error) throw new ApiError(error.message, 'storage_error');

  return { path, publicUrl: publicMediaUrl(client, path) };
}

/**
 * Deletes an uploaded object.
 *
 * Failure is deliberately non-fatal at the call site: a stale object wastes
 * storage but never breaks a menu, so callers treat this as best-effort.
 *
 * @param client A Supabase client.
 * @param path Object key returned by {@link uploadMedia}.
 */
export async function removeMedia(client: DbClient, path: string): Promise<void> {
  const { error } = await client.storage.from(MEDIA_BUCKET).remove([path]);
  if (error) throw new ApiError(error.message, 'storage_error');
}
